import { Controller } from "@hotwired/stimulus"
import tinymce from "tinymce"
import "tinymce/icons/default"
import "tinymce/themes/silver"
import "tinymce/models/dom"
import "tinymce/plugins/link"
import "tinymce/plugins/lists"
import "tinymce/plugins/code"
import "../vendor/tinymce/langs/it"

// Marker [foto: id="123"] cliccabili in anteprima dentro l'editor.
//
// L'evidenziazione è SOLO visiva, dentro l'iframe di editing: il testo
// del marker non viene mai alterato. Ad ogni SetContent (caricamento
// iniziale, undo/redo) il marker viene avvolto in uno <span> non
// editabile solo per lo stile/click; ad ogni GetContent (autosave verso
// la textarea, submit del form) quello stesso wrapper viene rimosso,
// lasciando il testo del marker esattamente come nel DB. Non si
// ri-avvolge durante la digitazione (keyup/change) per non rischiare di
// disturbare il cursore: un marker digitato a mano diventa cliccabile
// dopo aver riaperto/ricaricato la form.
const FOTO_MARKER_REGEX = /\[foto:\s*id="(\d+)"\]/g

function wrapFotoMarkers(editor) {
  const body = editor.getBody()
  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT)
  const textNodes = []
  let node

  while ((node = walker.nextNode())) {
    if (node.parentElement?.closest("[data-foto-marker]")) continue
    FOTO_MARKER_REGEX.lastIndex = 0
    if (FOTO_MARKER_REGEX.test(node.nodeValue)) textNodes.push(node)
  }

  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue
    const frag = document.createDocumentFragment()
    let lastIndex = 0
    let match

    FOTO_MARKER_REGEX.lastIndex = 0
    while ((match = FOTO_MARKER_REGEX.exec(text))) {
      if (match.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      }

      const span = document.createElement("span")
      span.className = "foto-marker-preview"
      span.setAttribute("data-foto-marker", "true")
      span.setAttribute("data-photo-id", match[1])
      span.setAttribute("contenteditable", "false")
      span.textContent = match[0]
      frag.appendChild(span)

      lastIndex = match.index + match[0].length
    }

    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)))
    }

    textNode.parentNode.replaceChild(frag, textNode)
  })
}

function unwrapFotoMarkers(html) {
  const div = document.createElement("div")
  div.innerHTML = html
  div.querySelectorAll("[data-foto-marker]").forEach((span) => {
    span.replaceWith(document.createTextNode(span.textContent))
  })
  return div.innerHTML
}

async function showFotoMarkerPreview(photoId) {
  let photo
  try {
    const response = await fetch(`/photos/${photoId}.json`, { headers: { Accept: "application/json" } })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    photo = await response.json()
  } catch (e) {
    console.error("Errore caricamento anteprima foto:", e)
    alert(`Impossibile caricare l'anteprima della foto (id ${photoId})`)
    return
  }

  const overlay = document.createElement("div")
  overlay.style.cssText = "position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:1rem;"
  overlay.innerHTML = `
    <div class="bg-secondary border-0 rounded shadow" style="max-width:600px;width:100%;">
      <div class="d-flex justify-content-between align-items-center p-3 bg-warning rounded-top">
        <h5 class="mb-0">${photo.title ?? ""}</h5>
        <button type="button" class="btn-close" data-role="close-foto-preview" aria-label="Chiudi"></button>
      </div>
      <div class="bg-white text-center p-3">
        <img src="${photo.large_url}" class="img-fluid rounded shadow" style="max-height:60vh;">
      </div>
      <div class="bg-white text-end p-2 rounded-bottom border-top">
        <a href="/photos/${photo.id}/photo_relations" target="_blank" rel="noopener" class="btn btn-info btn-sm">
          <i class="fas fa-link me-1"></i>Correla Foto
        </a>
      </div>
    </div>
  `
  const close = () => overlay.remove()
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
  overlay.querySelector("[data-role='close-foto-preview']").addEventListener("click", close)
  document.body.appendChild(overlay)
}

export default class extends Controller {
  static targets = ["input"]

  connect() {
    console.log("🟢 CONNECT", this.element.querySelector('textarea')?.id)
    
    // Piccolo delay per lasciar stabilizzare il DOM
    setTimeout(() => this.initTinymce(), 50)
  }

  disconnect() {
    console.log("🔴 DISCONNECT")
    // Rimuovi solo se l'elemento non è più nel DOM
    if (!document.contains(this.element)) {
      this.removeTinymce()
    }
  }

  initTinymce() {
    // Verifica che il controller sia ancora connesso
    if (!this.element.isConnected) {
      console.log("⚠️ Elemento non più nel DOM, skip init")
      return
    }

    const textarea = this.element.querySelector('textarea')
    if (!textarea) {
      console.error("❌ textarea non trovato")
      return
    }

    const id = textarea.id
    console.log("🚀 Init TinyMCE:", id)

    // Rimuovi istanza precedente
    tinymce.get(id)?.remove()

    tinymce.init({
      target: textarea,
      license_key: "gpl",
      language: "it",
      language_url: "/javascript/vendor/tinymce/langs/it.js",
      skin_url: "/assets/tinymce/skins/ui/oxide",
      content_css: "/assets/tinymce/skins/content/default/content.css",
      menubar: "format",
      branding: false,
      plugins: "link lists code",
      toolbar: "undo redo | styleselect | bold italic underline | bullist numlist | link | code",
      height: (textarea.rows || 10) * 30,
      setup: (editor) => {
        editor.on("init", () => {
          console.log("✅ TinyMCE READY:", id)
          editor.dom.addStyle(`
            .foto-marker-preview {
              background: #fff3cd;
              border: 1px solid #ffe69c;
              border-radius: 3px;
              padding: 0 3px;
              cursor: pointer;
            }
            .foto-marker-preview:hover {
              background: #ffe69c;
            }
          `)
        })
        editor.on("SetContent", () => wrapFotoMarkers(editor))
        editor.on("GetContent", (e) => { e.content = unwrapFotoMarkers(e.content) })
        editor.on("click", (e) => {
          const marker = e.target.closest?.("[data-foto-marker]")
          if (marker) showFotoMarkerPreview(marker.dataset.photoId)
        })
        editor.on("change keyup", () => editor.save())
      }
    })
  }

  removeTinymce() {
    const textarea = this.element.querySelector('textarea')
    if (!textarea) return
    tinymce.get(textarea.id)?.remove()
  }
}